import { MutableRefObject, useEffect, useState } from 'react'
import useIsMountedRef from 'use-is-mounted-ref'
import { Observable, Subscription } from 'rxjs'
import { ReactState, StateSetter } from '../Types/Types'
import { IsVarEmpty, isVarNotEmpty } from '../../../NonReact/utils/GlobalFunctions'
import { RxJSState, RxJSStateDefinable, RxJSStateWithResolver } from '../../../NonReact/common/Types/Types'

export type RxJSCurrentState<T extends unknown> = [Definable<T>, StateSetter<Definable<T>>]

/**
 * A hook that allows you to use RxJS state in a React component.
 *
 * @param state - The RxJS state to use.
 * @param defaultValue - The default value to use if the state is empty.
 * @param args
 */
export const useRxJSState = <T extends unknown, U extends () => void = () => void>(
    state: RxJSState<T> | RxJSStateDefinable<T> | RxJSStateWithResolver<T, U>,
    defaultValue?: Definable<T>,
    ...args: Parameters<U>
): RxJSCurrentState<T> => {
    const [value, setValue]: ReactState<Definable<T>> = useState<Definable<T>>()

    const isMounted: MutableRefObject<boolean> = useIsMountedRef()
    const [isDefaulted, setIsDefaulted]: ReactState<boolean> = useState<boolean>(false)

    useEffect((): (() => void) => {
        const isFunc: boolean = typeof state?.resolver === 'function'

        const observable: Observable<Definable<T>> = isFunc
            ? (state.observable as (...args: Parameters<U>) => Observable<Definable<T>>)?.(...args)
            : (state.observable as Observable<Definable<T>>)

        const subscription: Subscription = observable.subscribe((update: Definable<T>): void => {
            if (isMounted.current) {
                setValue(update)
            }
        })

        return (): void => subscription.unsubscribe()
    }, [state])

    useEffect((): void => {
        if (isVarNotEmpty(defaultValue) && IsVarEmpty(value) && !isDefaulted) {
            setIsDefaulted(true)
            state.announce(defaultValue)
        }
    }, [defaultValue, isDefaulted])

    return [
        value,
        (setter?: T | ((prev: Definable<T>) => Definable<T>)): void => {
            if (typeof setter === 'function') {
                state.announce((setter as (prev: T) => T)(value as T))
            } else {
                state.announce(setter as T)
            }
        }
    ]
}
